import React from 'react'

import { type Scope, createContextScope } from '@radix-ui/react-context'

import * as Comp from './option-menu.styles'

import { Divider } from './components/divider'
import { Footer } from './components/footer'
import { Group } from './components/group'
import { Item } from './components/item'
import { List } from './components/list'

import type { OptionMenuProps } from './option-menu.types'

type OptionMenuContext = Pick<OptionMenuProps, 'type'>

const DISPLAY_NAME: string = 'OptionMenu'

function Root(props: OptionMenuProps) {
  const {
    type,
    children,
    customEmptyMessage = 'Não foi encontrado nenhum resultado.',
    __scopeOptionMenu,
    testID,
  } = props as ScopedProps<OptionMenuProps>

  const renderChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      const isValidElementToRender =
        child.type === List || child.type === Footer

      if (isValidElementToRender) {
        return child
      }
    }
  })

  return (
    <OptionMenuProvider scope={__scopeOptionMenu} type={type}>
      <Comp.Root testID={testID}>
        {React.Children.count(children) > 0 ? (
          renderChildren
        ) : (
          <Comp.EmptyContainer>
            <Comp.EmptyText>{customEmptyMessage}</Comp.EmptyText>
          </Comp.EmptyContainer>
        )}
      </Comp.Root>
    </OptionMenuProvider>
  )
}

Root.displayName = DISPLAY_NAME

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeOptionMenu?: Scope
}

const [createOptionMenuContext] = createContextScope(DISPLAY_NAME)

export const [OptionMenuProvider, useOptionMenuContext]: readonly [
  React.FC<
    OptionMenuContext & {
      scope: Scope<OptionMenuContext>
      children: React.ReactNode
    }
  >,
  (consumerName: string, scope: Scope) => OptionMenuContext,
] = createOptionMenuContext<OptionMenuContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const OptionMenu = {
  Root,
  List,
  Item,
  Footer,
  Divider,
  Group,
}
